import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useGeoBrowseContext } from '../../context/GeoBrowseContext';
import { useGeoBrowseQuery } from '../../hooks/useGeoBrowseThresholds';
import {
  fetchComparisons,
  fetchPca,
  fetchStudyDetail,
  fetchStudySamples,
  fetchTopDegs,
  fetchVolcano,
} from '../../services/geoBrowseApi';
import { formatDiseaseName, formatNumber } from '../../utils/formatters';
import {
  buildStudyDetailPath,
  getStudyStatus,
  isWholeStudyComparison,
  wholeStudyComparisonId,
} from '../../utils/studyHelpers';
import BackLink from '../BackLink';
import StatCards from '../StatCards';
import EnrichmentPanel from './EnrichmentPanel';
import PcaPlot from './PcaPlot';
import TopDegsTable from './TopDegsTable';
import VolcanoPlot from './VolcanoPlot';
import { StatusBadge } from '../ui/GeoBrowseUi';
import styles from '../../GeoBrowse.module.css';

type StudyTab = 'volcano' | 'pca' | 'top-degs' | 'enrichment';

export default function StudyDetailView() {
  const { cui = '', accession = '' } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const comparisonId = searchParams.get('comparison_id') ?? undefined;
  const { thresholds, refreshKey, buildStudiesListPath, buildPathWithThresholds } = useGeoBrowseContext();
  const [activeTab, setActiveTab] = useState<StudyTab>('volcano');
  const [samplePage, setSamplePage] = useState(0);
  const samplesPerPage = 10;

  const {
    data: study,
    loading: studyLoading,
    error: studyError,
  } = useGeoBrowseQuery(
    () => fetchStudyDetail(cui, accession, comparisonId),
    [cui, accession, comparisonId, refreshKey],
    Boolean(cui && accession),
  );

  const { data: samples, loading: samplesLoading } = useGeoBrowseQuery(
    () => fetchStudySamples(cui, accession),
    [cui, accession, refreshKey],
    Boolean(cui && accession),
  );

  const { data: comparisons } = useGeoBrowseQuery(
    () => fetchComparisons(cui, accession),
    [cui, accession, refreshKey],
    Boolean(cui && accession),
  );

  const studyReady = Boolean(cui && accession);

  const { data: pca, loading: pcaLoading } = useGeoBrowseQuery(
    () => fetchPca(cui, accession),
    [cui, accession, refreshKey],
    studyReady,
  );

  const { data: topDegs, loading: topDegsLoading } = useGeoBrowseQuery(
    () => fetchTopDegs(cui, accession, thresholds, 50, comparisonId),
    [cui, accession, comparisonId, thresholds.padj, thresholds.fc, refreshKey],
    studyReady,
  );

  const { data: volcano, loading: volcanoLoading } = useGeoBrowseQuery(
    () => fetchVolcano(cui, accession, thresholds, comparisonId),
    [cui, accession, comparisonId, thresholds.padj, thresholds.fc, refreshKey],
    studyReady,
  );

  const sigCount = useMemo(
    () => volcano?.filter(p => p.significant).length ?? study?.n_genes_sig ?? 0,
    [volcano, study],
  );

  const activeComparisonId =
    comparisonId ?? study?.comparison_id ?? (cui && accession ? wholeStudyComparisonId(cui, accession) : '');

  const samplePageRows = useMemo(() => {
    if (!samples) return [];
    const start = samplePage * samplesPerPage;
    return samples.slice(start, start + samplesPerPage);
  }, [samples, samplePage]);

  const sampleTotalPages = samples ? Math.max(1, Math.ceil(samples.length / samplesPerPage)) : 1;

  if (!cui || !accession) {
    return (
      <div className={styles.empty}>
        Select a study from the Studies view to inspect volcano plots, PCA, and enrichment.
      </div>
    );
  }

  if (studyLoading) return <div className={styles.loading}>Loading study…</div>;
  if (studyError) return <div className={styles.error}>{studyError}</div>;
  if (!study) return <div className={styles.empty}>Study not found.</div>;

  const status = getStudyStatus(study);
  const tabs: { id: StudyTab; label: string }[] = [
    { id: 'volcano', label: 'Volcano' },
    { id: 'pca', label: 'PCA' },
    { id: 'top-degs', label: 'Top DEGs' },
    { id: 'enrichment', label: 'Enrichment' },
  ];

  return (
    <>
      <BackLink to={buildStudiesListPath({ cui })} label="Studies" />

      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderMain}>
          <div>
            <h1 className={styles.detailTitle}>{study.title}</h1>
            <p className={styles.detailSubtitle}>
              {formatDiseaseName(study.cui_name)} · {study.cui}
              {study.comparison_id ? ` · ${study.comparison_id}` : ''}
            </p>
          </div>
        </div>
        <div className={styles.pageHeaderActions}>
          <StatusBadge label={status.label} variant={status.variant} />
          <a
            className={styles.geoExternalBtn}
            href={`https://www.ncbi.nlm.nih.gov/geo/query/acc.cgi?acc=${study.accession}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View on GEO
            <ArrowTopRightOnSquareIcon width={14} height={14} aria-hidden />
          </a>
        </div>
      </div>

      <StatCards
        items={[
          {
            label: 'Accession',
            value: study.accession,
            sub: `${study.year} · ${study.platform}`,
          },
          {
            label: 'Samples',
            value: study.n_samples,
            sub: `${study.n_case} case / ${study.n_control} control`,
          },
          {
            label: 'QC flag rate',
            value: `${study.qc_flag_rate}%`,
            sub: `${study.confident_sex_mismatches} sex mismatches`,
          },
          {
            label: 'Significant genes',
            value: formatNumber(sigCount),
            sub: `padj ≤ ${thresholds.padj}, |LFC| ≥ ${thresholds.fc}`,
          },
        ]}
      />

      <div className={styles.studyDetailLayout}>
        <section className={styles.mainPanel}>
          <div className={styles.tabBar}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'volcano' && (
            <>
              {volcanoLoading && <div className={styles.tabLoading}>Loading volcano plot…</div>}
              {!volcanoLoading && volcano && (
                <>
                  <VolcanoPlot points={volcano} thresholds={thresholds} />
                  <p className={styles.chartCaption}>
                    {sigCount} of {volcano.length} genes significant at padj ≤ {thresholds.padj} and |log2FC|
                    ≥ {thresholds.fc}. Significant points are also separated by the dashed cut-off lines, not
                    colour alone.
                  </p>
                </>
              )}
            </>
          )}
          {activeTab === 'pca' && (
            <>
              {(pcaLoading || samplesLoading) && <div className={styles.tabLoading}>Loading PCA…</div>}
              {!pcaLoading && !samplesLoading && pca && <PcaPlot points={pca} samples={samples ?? []} />}
            </>
          )}
          {activeTab === 'top-degs' && (
            <div className={styles.tabPanel}>
              {topDegsLoading && <div className={styles.tabLoading}>Loading top DEGs…</div>}
              {!topDegsLoading && topDegs && topDegs.length > 0 && <TopDegsTable rows={topDegs} />}
              {!topDegsLoading && topDegs && topDegs.length === 0 && (
                <div className={styles.tabEmpty}>No significant genes at current thresholds.</div>
              )}
            </div>
          )}
          {activeTab === 'enrichment' && (
            <EnrichmentPanel cui={cui} accession={accession} comparisonId={comparisonId} />
          )}
        </section>

        <aside>
          <div className={styles.sectionCard}>
            <h2 className={styles.sectionCardTitle}>Study</h2>
            {comparisons && comparisons.length > 1 && (
              <label className={styles.comparisonSelectLabel}>
                Comparison
                <select
                  className={styles.comparisonSelect}
                  value={activeComparisonId}
                  onChange={e => {
                    const nextId = e.target.value;
                    navigate(buildPathWithThresholds(buildStudyDetailPath(cui, accession, nextId)));
                  }}
                >
                  {comparisons.map(c => (
                    <option key={c.comparison_id} value={c.comparison_id}>
                      {isWholeStudyComparison(c) ? 'Whole study' : c.comparison_id}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <ul className={styles.metaList}>
              <li>
                <span>Design</span>
                <span>{study.overall_design || '—'}</span>
              </li>
              <li>
                <span>Platform</span>
                <span>{study.platform}</span>
              </li>
              <li>
                <span>Year</span>
                <span>{study.year}</span>
              </li>
              <li>
                <span>Usable for DEG</span>
                <span>{study.usable_for_deg ? 'Yes' : 'No'}</span>
              </li>
              <li>
                <span>DEG results</span>
                <span>{study.has_deg ? 'Available' : 'None'}</span>
              </li>
              <li>
                <span>Group flags</span>
                <span>{study.confident_group_consistency_flags}</span>
              </li>
            </ul>
          </div>

          {samples && samples.length > 0 && (
            <div className={styles.sectionCard}>
              <h2 className={styles.sectionCardTitle}>Samples</h2>
              <div className={styles.samplesTableWrap}>
                <table className={styles.samplesMiniTable}>
                  <thead>
                    <tr>
                      <th>Sample</th>
                      <th>Group</th>
                      <th>Sex</th>
                    </tr>
                  </thead>
                  <tbody>
                    {samplePageRows.map(s => (
                      <tr key={s.sample_id}>
                        <td>{s.sample_id}</td>
                        <td>
                          <StatusBadge label={s.group} variant={s.group === 'case' ? 'case' : 'neutral'} />
                        </td>
                        <td>{s.sex}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className={styles.tableFooter}>
                <span>
                  {samplePage * samplesPerPage + 1}-
                  {Math.min((samplePage + 1) * samplesPerPage, samples.length)} of {samples.length}
                </span>
                <div className={styles.samplesPagination}>
                  <button
                    type="button"
                    className={styles.pageIconBtn}
                    disabled={samplePage === 0}
                    onClick={() => setSamplePage(p => p - 1)}
                    aria-label="Previous samples page"
                  >
                    <ChevronLeftIcon width={16} height={16} aria-hidden />
                  </button>
                  <span>
                    {samplePage + 1}/{sampleTotalPages}
                  </span>
                  <button
                    type="button"
                    className={styles.pageIconBtn}
                    disabled={samplePage >= sampleTotalPages - 1}
                    onClick={() => setSamplePage(p => p + 1)}
                    aria-label="Next samples page"
                  >
                    <ChevronRightIcon width={16} height={16} aria-hidden />
                  </button>
                </div>
              </div>
            </div>
          )}
        </aside>
      </div>
    </>
  );
}
